import React, { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'oauth';
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

/**
 * Button component based on V4 Step 1 authentication design
 * Supports primary (blue), secondary (white), and OAuth variants
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      variant = 'primary',
      size = 'medium',
      loading = false,
      icon,
      fullWidth = false,
      disabled,
      ...props
    },
    ref
  ) => {
    // Base styles that apply to all buttons
    const baseStyles = [
      'inline-flex',
      'items-center',
      'justify-center',
      'font-medium',
      'rounded-lg',
      'transition-all',
      'duration-200',
      'ease-in-out',
      'cursor-pointer',
      'relative',
      'border',
      fullWidth && 'w-full',
      disabled && 'opacity-50 cursor-not-allowed',
      !disabled && 'hover:transform hover:-translate-y-px',
    ];

    // Variant-specific styles matching V4 Step 1 design
    const variantStyles = {
      primary: [
        'bg-[var(--auth-button-primary-bg)]',
        'text-white',
        'border-transparent',
        !disabled && 'hover:bg-[var(--auth-button-primary-hover)]',
        !disabled && 'hover:shadow-lg',
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-[var(--auth-accent-blue)]',
        'focus:ring-offset-2',
      ],
      secondary: [
        'bg-[var(--auth-button-secondary-bg)]',
        'text-[var(--auth-text-primary)]',
        'border-[var(--auth-button-secondary-border)]',
        !disabled && 'hover:bg-[var(--auth-button-secondary-hover)]',
        !disabled && 'hover:border-[var(--auth-border-focus)]',
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-[var(--auth-accent-blue)]',
        'focus:ring-offset-2',
      ],
      oauth: [
        'bg-white',
        'text-[var(--auth-text-primary)]',
        'border-[var(--auth-border-primary)]',
        !disabled && 'hover:bg-gray-50',
        !disabled && 'hover:border-[var(--auth-border-focus)]',
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-[var(--auth-accent-blue)]',
        'focus:ring-offset-2',
      ],
    };

    // Size-specific styles
    const sizeStyles = {
      small: ['px-3', 'py-1.5', 'text-sm', 'gap-1.5'],
      medium: ['px-4', 'py-3', 'text-sm', 'gap-2'],
      large: ['px-6', 'py-3.5', 'text-base', 'gap-2.5'],
    };

    return (
      <button
        ref={ref}
        className={clsx(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <span className="absolute inset-0 flex items-center justify-center">
              <svg
                className="animate-spin h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </span>
            <span className="opacity-0">{children}</span>
          </>
        ) : (
          <>
            {icon && <span className="flex-shrink-0">{icon}</span>}
            {children}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
